import React, { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import StaffSectionLayout from '../../../components/staff/StaffSectionLayout';
import ContentBox from '../../../components/ContentBox';
import { FpButton, TextInput, NumberInput, RarityPill } from '../../../components/frankopop/frankopopShared';
import {
    useFrankopopData, fetchRarities,
    adminCreateRarity, adminUpdateRarity, adminSetBaseRarity, adminDeleteRarity,
} from '../../../data/frankopop';

function RarityRow({ rarity, onChanged }) {
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState(rarity.name);
    const [chance, setChance] = useState(rarity.chancePercent);
    const [scrapValue, setScrapValue] = useState(rarity.scrapValue);

    const save = async () => {
        const res = await adminUpdateRarity(rarity.id, { name, chancePercent: chance, scrapValue });
        if (!res.success) { toast.error(res.message); return; }
        toast.success('Rarity saved.');
        setEditing(false);
        onChanged();
    };

    const remove = async () => {
        const res = await adminDeleteRarity(rarity.id);
        if (!res.success) toast.error(res.message);
        else { toast.success('Rarity deleted.'); onChanged(); }
    };

    const makeBase = async () => {
        const res = await adminSetBaseRarity(rarity.id);
        if (!res.success) { toast.error(res.message); return; }
        toast.success(`${rarity.name} is now the base rarity.`);
        onChanged();
    };

    return (
        <div className="flex flex-wrap items-center gap-3 border border-gray-200 bg-white px-3 py-2" style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.08)' }}>
            <RarityPill name={rarity.name} colour={rarity.colour} />
            {editing ? (
                <>
                    <TextInput value={name} onChange={(e) => setName(e.target.value)} className="flex-1 min-w-[140px]" autoFocus />
                    {!rarity.isBaseRarity && (
                        <div className="flex items-center gap-1">
                            <NumberInput min={0} max={100} step="0.1" value={chance} onChange={(e) => setChance(e.target.value)} className="w-24" />
                            <span className="text-xs text-gray-400">%</span>
                        </div>
                    )}
                    <div className="flex items-center gap-1">
                        <NumberInput min={0} step="1" value={scrapValue} onChange={(e) => setScrapValue(e.target.value)} className="w-20" />
                        <span className="text-xs text-gray-400">scraps</span>
                    </div>
                    <FpButton size="sm" variant="primary" onClick={save}>Save</FpButton>
                    <FpButton size="sm" variant="secondary" onClick={() => { setName(rarity.name); setChance(rarity.chancePercent); setScrapValue(rarity.scrapValue); setEditing(false); }}>Cancel</FpButton>
                </>
            ) : (
                <>
                    <span className="flex-1 font-['Jersey_10'] uppercase text-[15px] tracking-[1px] text-gray-800">{rarity.name}</span>
                    {rarity.isBaseRarity ? (
                        <span className="font-['Jersey_10'] uppercase text-[11px] tracking-[1px] px-2 py-0.5 bg-amber-200 text-amber-800">
                            Base - fills the remainder
                        </span>
                    ) : (
                        <span className="font-['Jersey_10'] text-[14px] text-maroon">{rarity.chancePercent}%</span>
                    )}
                    <span className="font-['Jersey_10'] uppercase text-[11px] tracking-[1px] text-gray-500">
                        {rarity.scrapValue} scraps
                    </span>
                    <span className="text-[11px] font-['Jersey_10'] uppercase tracking-[1px] text-gray-400">
                        {rarity.inUse ? 'In use' : 'Unused'}
                    </span>
                    <FpButton size="sm" variant="ghost" onClick={() => setEditing(true)}>Edit</FpButton>
                    {!rarity.isBaseRarity && (
                        <FpButton size="sm" variant="ghost" onClick={makeBase}>Set as base</FpButton>
                    )}
                    <FpButton size="sm" variant="danger" disabled={rarity.inUse || rarity.isBaseRarity} onClick={remove}>Delete</FpButton>
                </>
            )}
        </div>
    );
}

export default function FrankoPopRaritiesAdmin() {
    const { data: rarities, loading, refetch } = useFrankopopData(useCallback(() => fetchRarities(), []), []);
    const [newName, setNewName] = useState('');
    const [newChance, setNewChance] = useState(10);
    const [newScrapValue, setNewScrapValue] = useState(2);

    const add = async () => {
        const res = await adminCreateRarity(newName, newChance, newScrapValue);
        if (!res.success) { toast.error(res.message); return; }
        toast.success('Rarity created.');
        setNewName('');
        setNewChance(10);
        setNewScrapValue(2);
        refetch();
    };

    const list = rarities || [];
    const nonBaseSum = list.filter((r) => !r.isBaseRarity).reduce((n, r) => n + Number(r.chancePercent), 0);

    return (
        <StaffSectionLayout>
            <ContentBox theme="default" tint="red" header="FrankoPop - rarity types">
                <p className="text-sm text-gray-600 mb-2">
                    Chance is <strong>global</strong> - the same across every set, not configured per set. The base
                    rarity (the amber one, Common by default) has no fixed chance of its own: in any given set, it
                    always fills whatever the other rarities actually present in that set don&apos;t use, up to 100%.
                    A rarity a set has no figures of can&apos;t affect that remainder.
                </p>
                <p className="text-sm text-gray-600 mb-4">
                    Non-base rarities currently add up to <strong>{Math.round(nonBaseSum * 100) / 100}%</strong> when
                    all of them are present together in the same set - the base rarity would take the rest. Deleting
                    a rarity is blocked while any FrankoPop uses it, and the base rarity can&apos;t be deleted at all
                    (make another rarity the base first). Scrap value is also global and applies to every rarity,
                    base included - it&apos;s how many FrankoPop Scraps a player earns for quick selling one of that
                    rarity.
                </p>

                {loading ? (
                    <p className="text-sm text-gray-500 mb-5">Loading rarities...</p>
                ) : (
                    <div className="space-y-2 mb-5">
                        {list.map((r) => <RarityRow key={r.id} rarity={r} onChanged={refetch} />)}
                    </div>
                )}

                <div className="border-t border-gray-200 pt-4">
                    <label className="block font-['Jersey_10'] uppercase text-[13px] tracking-[1px] text-gray-700 mb-2">Add a rarity</label>
                    <div className="flex flex-wrap gap-2">
                        <TextInput
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            placeholder="e.g. Ultra Rare"
                            className="flex-1 min-w-[160px]"
                            onKeyDown={(e) => e.key === 'Enter' && add()}
                        />
                        <div className="flex items-center gap-1">
                            <NumberInput min={0} max={100} step="0.1" value={newChance} onChange={(e) => setNewChance(e.target.value)} className="w-24" />
                            <span className="text-xs text-gray-400">%</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <NumberInput min={0} step="1" value={newScrapValue} onChange={(e) => setNewScrapValue(e.target.value)} className="w-20" />
                            <span className="text-xs text-gray-400">scraps</span>
                        </div>
                        <FpButton variant="primary" onClick={add} disabled={!newName.trim()}>Add</FpButton>
                    </div>
                </div>
            </ContentBox>
        </StaffSectionLayout>
    );
}
